import type { ErrorComponentProps } from "@tanstack/react-router";
import { useState } from "react";

/**
 * Page-level error screen for every route (replaces the router's bare "Something went wrong!").
 * Says what failed in plain words and offers ways forward that work without the failed view;
 * remote and locked-down browsers (email link isolation) are the usual cause in the field.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const [detail, setDetail] = useState(false);
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="flex h-full items-center justify-center p-6" data-testid="route-error">
      <div className="max-w-md text-[13px]" style={{ color: "var(--muted)" }}>
        <h1 className="mb-2 text-[16px] font-semibold" style={{ color: "var(--paper)" }}>
          This page could not load
        </h1>
        <p className="mb-3">
          Something in this browser stopped the page. Reloading often helps. If the link came from
          an email and opened inside a security viewer (for example Mimecast browser isolation),
          copy plenumatlas.com into your browser's address bar instead.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => {
              reset();
              window.location.reload();
            }}
          >
            Reload
          </button>
          <a className="btn btn-sm" href="/table">
            Table
          </a>
          <a className="btn btn-sm" href="/explore">
            Explore
          </a>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setDetail((d) => !d)}
          >
            {detail ? "Hide detail" : "Detail"}
          </button>
        </div>
        {detail ? (
          <pre
            className="num mt-3 max-h-40 overflow-auto whitespace-pre-wrap text-[11px]"
            style={{ color: "var(--muted-2)" }}
          >
            {message}
          </pre>
        ) : null}
      </div>
    </div>
  );
}
